'use client'

import { Fragment, useEffect, useRef, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { Dialog, DialogPanel, Disclosure, DisclosureButton, DisclosurePanel, Transition, TransitionChild } from '@headlessui/react'
import { ArrowRight, ChevronDown, ChevronRight, Heart, LayoutGrid, Medal, Menu, Search, ShieldAlert, ShoppingBag, ShoppingCart, Sparkles, Trash2, User as UserIcon, Wallet, X } from 'lucide-react'
import Avatar from 'boring-avatars'
import { useCart, useNavigation, useSearch } from '@/lib/commerce/hooks'
import { formatImageURL, formatPrice, getProductDisplayPrice, isCustomerAuthActive } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import CartItemSidebar from '../cart/CartItemSidebar'
import RewardSelector from '../cart/RewardSelector'
import SubHeader from './SubHeader'

function MegaMenu({ section }: { section: any, pathname: string }) {
  const items = section.items || []
  const [hoveredChild, setHoveredChild] = useState(0)
  const child = items[hoveredChild]
  return (
    <div
      className="absolute left-0 right-0 top-full z-50"
      style={{
        background: 'rgba(8,8,10,0.96)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderTop: '1px solid rgba(255,255,255,0.07)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        boxShadow: '0 24px 60px -10px rgba(0,0,0,0.6)',
      }}
    >
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 py-8 grid grid-cols-12 gap-8">
        <div className="col-span-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-4" style={{ color: '#a8a8b3' }}>
            {section.name}
          </div>
          {items.length > 0 ? (
            <ul className="space-y-1 -mx-2">
              {items.map((c: any, i: number) => (
                <li key={c.name}>
                  <button
                    onMouseEnter={() => setHoveredChild(i)}
                    onClick={() => { window.location.href = c.href }}
                    className="w-full flex items-center justify-between gap-2 px-2 py-2 rounded-md text-left transition-colors"
                    style={{
                      background: i === hoveredChild ? 'rgba(255,255,255,0.06)' : 'transparent',
                      color: i === hoveredChild ? 'white' : 'rgba(255,255,255,0.75)',
                    }}
                  >
                    <span className="text-[13px] font-medium truncate">{c.name}</span>
                    <ChevronRight className="h-3 w-3 shrink-0" style={{ opacity: i === hoveredChild ? 1 : 0.4 }} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-[13px]" style={{ color: 'rgba(255,255,255,0.5)' }}>Дэд ангилал байхгүй</div>
          )}
          <a
            href={section.href}
            className="mt-5 inline-flex items-center gap-1 text-[12px] font-medium px-2 py-1.5 rounded-md text-white transition-colors hover:bg-white/5"
          >
            Бүх {section.name.toLowerCase()} үзэх <ArrowRight className="h-3 w-3" />
          </a>
        </div>
        <div className="col-span-8">
          {child ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>
                  {child.name}
                </div>
                <a href={child.href} className="text-[12px] font-medium inline-flex items-center gap-1 text-white hover:opacity-80 transition-opacity">
                  Бүгдийг үзэх <ArrowRight className="h-3 w-3" />
                </a>
              </div>
              {child.children && child.children.length > 0 ? (
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {child.children.map((item: any) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="block text-[13px] py-1.5 transition-colors hover:text-white truncate"
                      style={{ color: 'rgba(255,255,255,0.7)' }}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-[13px]" style={{ color: 'rgba(255,255,255,0.5)' }}>Дэд ангилал байхгүй</div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function AllMenu({ sections, pathname }: { sections: any[], pathname: string }) {
  return (
    <div
      className="absolute left-0 right-0 top-full z-50"
      style={{
        background: 'rgba(8,8,10,0.96)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderTop: '1px solid rgba(255,255,255,0.07)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        boxShadow: '0 24px 60px -10px rgba(0,0,0,0.6)',
      }}
    >
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-2" style={{ color: '#a8a8b3' }}>Бүх ангилал</div>
            <h3 className="text-[26px] font-semibold tracking-[-0.02em] leading-none text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
              Яг юу хайж байна вэ?
            </h3>
          </div>
          <a
            href={navigateUrl('/product', pathname)}
            className="text-[12px] inline-flex items-center gap-1.5 px-4 h-8 rounded-full"
            style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}
          >
            Бүх бүтээгдэхүүн <ArrowRight className="h-3 w-3" />
          </a>
        </div>
        {sections.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {sections.map((cat, i) => (
              <div key={cat.id || cat.name}>
                <div className="flex items-center gap-2 pb-3 mb-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <span className="text-[10px] font-mono tabular-nums tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <a
                    href={cat.href}
                    className="font-semibold tracking-[-0.01em] text-white hover:opacity-80 transition-opacity truncate"
                    style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 17 }}
                  >
                    {cat.name}
                  </a>
                </div>
                <ul className="space-y-2.5">
                  {(cat.items || []).slice(0, 6).map((c: any) => (
                    <li key={c.name}>
                      <a href={c.href} className="text-[13px] transition-colors hover:text-white truncate block" style={{ color: 'rgba(255,255,255,0.65)' }}>
                        {c.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-[13px]" style={{ color: 'rgba(255,255,255,0.5)' }}>Ангилал олдсонгүй</div>
        )}
      </div>
    </div>
  )
}

function SearchPanel({ pathname, isAuthed, onClose, search }: { pathname: string, isAuthed: boolean, onClose: () => void, search: ReturnType<typeof useSearch> }) {
  const { query, results, categories: searchCategories, loading, history, runSearch, setQuery, clearHistory } = search
  return (
    <div
      className="absolute top-full left-0 right-0 mt-2 rounded-2xl overflow-hidden z-40"
      style={{
        background: 'rgba(8,8,10,0.96)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid rgba(255,255,255,0.14)',
        boxShadow: '0 24px 60px -10px rgba(0,0,0,0.7)',
      }}
      onMouseDown={e => e.preventDefault()}
    >
      {query.trim() ? (
        loading ? (
          <div className="px-5 py-6 text-[13px]" style={{ color: 'rgba(255,255,255,0.5)' }}>Хайж байна...</div>
        ) : results.length === 0 && searchCategories.length === 0 ? (
          <div className="px-5 py-6 text-[13px]" style={{ color: 'rgba(255,255,255,0.5)' }}>Илэрц олдсонгүй</div>
        ) : (
          <>
            {searchCategories.length > 0 && (
              <div className="p-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: '#a8a8b3' }}>Ангилал</div>
                <div className="flex flex-wrap gap-2">
                  {searchCategories.map((cat: any) => (
                    <a
                      key={cat.id}
                      href={navigateUrl(`/product?category=${cat.slug || cat.id}`, pathname)}
                      onClick={onClose}
                      className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full transition-colors hover:bg-white/10"
                      style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.85)' }}
                    >
                      <LayoutGrid className="h-3.5 w-3.5 shrink-0" style={{ color: '#a8a8b3' }} />
                      <span className="truncate max-w-40">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
            {results.length > 0 && (
              <>
                <div className="p-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: '#a8a8b3' }}>Бүтээгдэхүүн</div>
                  <ul className="space-y-1">
                    {results.slice(0, 6).map((product: any) => {
                      const variant = product?.variant
                      const imageUrl = variant?.images?.length > 0 ? formatImageURL(variant.images[0]) : product?.images?.[0] ? formatImageURL(product.images[0]) : formatImageURL('placeholder.png')
                      const price = getProductDisplayPrice(product, variant, isAuthed)
                      return (
                        <li key={product.id}>
                          <a
                            href={navigateUrl(`/product/${product.customId || product.id}`, pathname)}
                            onClick={onClose}
                            className="flex items-center gap-3 px-2 py-2 -mx-2 rounded-lg transition-colors hover:bg-white/5"
                          >
                            <div className="w-11 h-11 rounded-md overflow-hidden shrink-0" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
                              <img src={imageUrl} alt={product?.name} className="w-full h-full object-cover" />
                            </div>
                            <div className="min-w-0 flex-1">
                              {variant?.name && <div className="text-[10px] font-semibold uppercase tracking-[0.16em] truncate" style={{ color: '#a8a8b3' }}>{variant.name}</div>}
                              <div className="text-[13px] font-medium text-white truncate">{product?.name}</div>
                            </div>
                            <div className="text-[13px] font-semibold tabular-nums text-white shrink-0">{formatPrice(price)}</div>
                          </a>
                        </li>
                      )
                    })}
                  </ul>
                </div>
                <button
                  type="button"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => { runSearch(); onClose() }}
                  className="w-full px-5 py-3 text-[13px] font-medium text-left transition-colors hover:bg-white/5 inline-flex items-center justify-between"
                  style={{ color: 'white' }}
                >
                  <span>&quot;{query}&quot; гэсэн бүх илэрцийг үзэх</span>
                  <ArrowRight className="h-3 w-3" />
                </button>
              </>
            )}
          </>
        )
      ) : history.length > 0 ? (
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: '#a8a8b3' }}>Сүүлд хайсан</div>
            <button onMouseDown={e => e.preventDefault()} onClick={clearHistory} className="text-[11px] hover:text-white transition-colors" style={{ color: 'rgba(255,255,255,0.55)' }}>Цэвэрлэх</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {history.map(h => (
              <button
                key={h}
                onMouseDown={e => e.preventDefault()}
                onClick={() => { setQuery(h); runSearch(h); onClose() }}
                className="text-[12px] px-3 py-1.5 rounded-full transition-colors hover:bg-white/10"
                style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.85)' }}
              >
                {h}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="px-5 py-6 text-[13px]" style={{ color: 'rgba(255,255,255,0.5)' }}>Хайлтаа эхлүүлээрэй</div>
      )}
    </div>
  )
}

function MobileDrawer({ open, onClose, links, sections, pathname, showLogin, vendor }: { open: boolean, onClose: () => void, links: any[], sections: any[], pathname: string, showLogin: boolean, vendor?: any }) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-40 lg:hidden" onClose={onClose}>
        <TransitionChild as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/60" />
        </TransitionChild>
        <div className="fixed inset-0 z-40 flex">
          <TransitionChild as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="-translate-x-full" enterTo="translate-x-0" leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="-translate-x-full">
            <DialogPanel className="relative flex w-[86%] max-w-sm flex-col shadow-2xl rounded-r-2xl overflow-hidden" style={{ background: '#0b0b0e' }}>
              <div className="h-14 px-5 flex items-center justify-between shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <a href={navigateUrl('/', pathname)} onClick={onClose} className="flex items-center gap-2">
                  {vendor?.logo ? (
                    <img src={formatImageURL(vendor.logo)} alt={vendor?.name} className="h-6 max-w-32 w-auto object-contain" />
                  ) : (
                    <span className="text-[18px] font-semibold text-white tracking-[-0.02em]" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{vendor?.name || 'Shop'}</span>
                  )}
                </a>
                <button onClick={onClose} aria-label="Хаах" className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-colors hover:bg-white/5" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto overscroll-contain">
                {links.length > 0 && (
                  <ul className="px-2 py-3">
                    {links.map((item: any) => (
                      <li key={item.name}>
                        <a
                          href={navigateUrl(item.href, pathname)}
                          onClick={onClose}
                          className="flex items-center justify-between rounded-xl px-3 h-12 text-[15px] font-medium transition-colors hover:bg-white/5"
                          style={{ color: item.isSale ? '#fca5a5' : 'white' }}
                        >
                          <span>{item.name}</span>
                          <ChevronRight className="h-4 w-4 opacity-40" />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
                {sections.length > 0 && (
                  <div className="px-3 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="text-[10px] tracking-[0.2em] uppercase font-semibold px-2 mb-2" style={{ color: '#a8a8b3' }}>Ангилал</div>
                    {sections.map((s: any) => (
                      <Disclosure key={s.id || s.name} as="div" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }} className="last:border-0">
                        {({ open: catOpen }) => (
                          <>
                            <DisclosureButton className="w-full flex items-center justify-between rounded-xl px-3 h-12 text-[15px] font-medium transition-colors hover:bg-white/5" style={{ color: 'white' }}>
                              <span>{s.name}</span>
                              <ChevronDown className={`h-4 w-4 transition-transform ${catOpen ? 'rotate-180' : ''}`} style={{ opacity: 0.5 }} />
                            </DisclosureButton>
                            <DisclosurePanel as="ul" className="pl-3 pb-2 space-y-1">
                              <li>
                                <a href={s.href} onClick={onClose} className="block rounded-lg px-3 py-2 text-[14px] transition-colors hover:text-white hover:bg-white/5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                  Бүх {s.name.toLowerCase()}
                                </a>
                              </li>
                              {(s.items || []).map((child: any, i: number) => (
                                <li key={i}>
                                  <a href={child.href} onClick={onClose} className="block rounded-lg px-3 py-2 text-[14px] transition-colors hover:text-white hover:bg-white/5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                    {child.name}
                                  </a>
                                </li>
                              ))}
                            </DisclosurePanel>
                          </>
                        )}
                      </Disclosure>
                    ))}
                  </div>
                )}
              </div>
              {showLogin && (
                <div className="p-4 shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                  <a
                    href={navigateUrl('/login', pathname)}
                    onClick={onClose}
                    className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full text-[15px] font-semibold transition-colors"
                    style={{ background: 'white', color: '#0a0a0c' }}
                  >
                    <UserIcon className="h-4 w-4" />Нэвтрэх
                  </a>
                </div>
              )}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}

function CartDrawer({ open, onClose, pathname, vendor }: { open: boolean, onClose: () => void, pathname: string, vendor: any }) {
  const { data: session } = useSession()
  const { initialLoading, cartCount, cart, canCheckout, amount, sale, orderDiscountAmount, cartTotal, loyaltyDiscountAmount, freeShippingProgress, activeOrderDiscount, appliedCoupon, clearCart, loading } = useCart()
  const cartItems = cart?.products || []
  const loyaltyDiscount = loyaltyDiscountAmount > 0 ? Math.min(loyaltyDiscountAmount, Math.max(0, cartTotal - (orderDiscountAmount || 0))) : 0
  const total = Math.max(0, cartTotal - (orderDiscountAmount || 0) - loyaltyDiscount)
  const showShipping = freeShippingProgress && !cartItems.every((p: any) => p.isDigital === true)
  const subPrice = (amount || 0) - (sale || 0)
  const showMinOrder = vendor?.orderMinAmount > 0 && subPrice < vendor.orderMinAmount
  const minOrderPct = showMinOrder ? Math.min((subPrice / vendor.orderMinAmount) * 100, 100) : 0

  const orderDisc = activeOrderDiscount?.type === 'order' && !appliedCoupon && activeOrderDiscount.minOrderAmount > 0
    ? (() => {
      const targets = activeOrderDiscount.targets || []
      const target = targets.find((t: any) => t.role === 'target' && t.targetId === null)
        || targets.find((t: any) => t.role === 'target')
        || targets[0]
      const label = target?.valueType === 'percent' && target?.valuePercent
        ? `${target.valuePercent}%-ийн хөнгөлөлт`
        : target?.valueType === 'amount' && target?.valueAmount
          ? `${formatPrice(target.valueAmount)} хөнгөлөлт`
          : activeOrderDiscount.valueType === 'percent' && activeOrderDiscount.valuePercent
            ? `${activeOrderDiscount.valuePercent}%-ийн хөнгөлөлт`
            : activeOrderDiscount.valueType === 'amount' && activeOrderDiscount.valueAmount
              ? `${formatPrice(activeOrderDiscount.valueAmount)} хөнгөлөлт`
              : activeOrderDiscount.name || 'Захиалгын хөнгөлөлт'
      return { minAmt: activeOrderDiscount.minOrderAmount, label, pct: Math.min((subPrice / activeOrderDiscount.minOrderAmount) * 100, 100), reached: subPrice >= activeOrderDiscount.minOrderAmount }
    })()
    : null
  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/60" />
        </TransitionChild>
        <div className="fixed inset-0 z-50 flex justify-end">
          <TransitionChild as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="translate-x-full" enterTo="translate-x-0" leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="translate-x-full">
            <DialogPanel className="relative flex w-full max-w-lg flex-col overflow-hidden shadow-2xl" style={{ background: '#0b0b0e' }}>
              <div className="px-6 py-5 flex items-center justify-between gap-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white' }}
                  >
                    <ShoppingCart className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-white leading-none" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>Сагс</div>
                    {!initialLoading && cartCount > 0 && (
                      <div className="text-[11px] mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>{cartCount} бараа</div>
                    )}
                  </div>
                </div>
                <button onClick={onClose} aria-label="Хаах" className="w-8 h-8 inline-flex items-center justify-center rounded-full transition-colors hover:bg-white/5 cursor-pointer shrink-0" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  <X className="h-4 w-4" />
                </button>
              </div>

              {cartCount > 0 && (showShipping || orderDisc || showMinOrder) && (
                <div className="px-6 py-4 space-y-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  {showShipping && (
                    <div>
                      <div className="flex items-center justify-between text-[11.5px]">
                        {freeShippingProgress.reached ? (
                          <span className="font-medium" style={{ color: '#34d399' }}>Үнэгүй хүргэлт хангагдсан</span>
                        ) : (
                          <span className="text-white">
                            Үнэгүй хүргэлтэд <span className="font-semibold tabular-nums">{formatPrice(freeShippingProgress.remaining)}</span> үлдсэн
                          </span>
                        )}
                        <span className="tabular-nums" style={{ color: 'rgba(255,255,255,0.55)' }}>
                          {Math.min(100, Math.round(freeShippingProgress.percentage))}%
                        </span>
                      </div>
                      <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${Math.min(100, freeShippingProgress.percentage)}%`, background: '#34d399' }}
                        />
                      </div>
                    </div>
                  )}
                  {orderDisc && (
                    <div>
                      <div className="flex items-center justify-between text-[11.5px] gap-3">
                        {orderDisc.reached ? (
                          <span className="font-medium truncate" style={{ color: '#d8b4fe' }}>
                            {orderDisc.label} хангагдсан{orderDiscountAmount > 0 ? ` · −${formatPrice(orderDiscountAmount)}` : ''}
                          </span>
                        ) : (
                          <span className="text-white truncate">
                            {orderDisc.label}-д <span className="font-semibold tabular-nums">{formatPrice(orderDisc.minAmt - subPrice)}</span> үлдсэн
                          </span>
                        )}
                        <span className="tabular-nums shrink-0" style={{ color: 'rgba(255,255,255,0.55)' }}>
                          {Math.round(orderDisc.pct)}%
                        </span>
                      </div>
                      <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${orderDisc.pct}%`, background: '#a855f7' }}
                        />
                      </div>
                    </div>
                  )}
                  {showMinOrder && (
                    <div>
                      <div className="flex items-center justify-between text-[11.5px]">
                        <span className="text-white">
                          Захиалгын доод дүнд <span className="font-semibold tabular-nums">{formatPrice(vendor.orderMinAmount - subPrice)}</span> үлдсэн
                        </span>
                        <span className="tabular-nums" style={{ color: 'rgba(255,255,255,0.55)' }}>
                          {Math.round(minOrderPct)}%
                        </span>
                      </div>
                      <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${minOrderPct}%`, background: '#60a5fa' }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="flex-1 px-6 py-2 overflow-y-auto">
                {initialLoading ? (
                  <div className="space-y-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="h-16 rounded-lg animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
                    ))}
                  </div>
                ) : cartCount > 0 ? (
                  <div className="space-y-5">
                    {cartItems.map((product: any) => (
                      <CartItemSidebar
                        key={`${product.isRewardItem ? 'reward-' : ''}${product.id}-${product.variant?.id}`}
                        product={product}
                        pathname={pathname}
                      />
                    ))}
                    <RewardSelector compact />
                    <button
                      type="button"
                      onClick={clearCart}
                      disabled={loading}
                      className="w-full inline-flex items-center justify-center gap-2 text-[12px] font-medium cursor-pointer transition-opacity hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed pt-2"
                      style={{ color: '#fda4af' }}
                    >
                      <Trash2 className="h-3 w-3" />
                      Сагсыг хоослох
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16">
                    <ShoppingBag className="h-12 w-12 mb-3" style={{ color: 'rgba(255,255,255,0.3)' }} />
                    <p className="text-[14px]" style={{ color: 'rgba(255,255,255,0.6)' }}>Таны сагс хоосон байна</p>
                    <a
                      href={navigateUrl('/product', pathname)}
                      onClick={onClose}
                      className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-full text-[13px] font-medium"
                      style={{ background: 'white', color: '#0a0a0c' }}
                    >
                      <ShoppingBag className="h-4 w-4" /> Дэлгүүр хэсэх
                    </a>
                  </div>
                )}
              </div>
              {cartCount > 0 && (
                <div className="px-6 py-5 space-y-2.5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex items-center justify-between text-[13px]" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    <span>Нийт дүн</span>
                    <span className="text-white text-[16px] font-semibold tabular-nums">{formatPrice(total)}</span>
                  </div>
                  <a
                    href={navigateUrl('/cart', pathname)}
                    onClick={onClose}
                    className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-full text-[13px] font-medium transition-colors"
                    style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.14)' }}
                  >
                    <ShoppingCart className="h-4 w-4" /> Сагс үзэх
                  </a>
                  <button
                    onClick={() => {
                      onClose()
                      if (session?.user?.id || vendor?.canGuestOrder === true || !isCustomerAuthActive(vendor)) {
                        window.location.href = navigateUrl('/checkout', pathname)
                      } else {
                        window.location.href = navigateUrl('/checkout/login', pathname)
                      }
                    }}
                    disabled={!canCheckout()}
                    className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-full text-[13px] font-semibold transition-opacity disabled:opacity-50"
                    style={{ background: 'white', color: '#0a0a0c' }}
                  >
                    <Wallet className="h-4 w-4" /> Худалдан авах
                  </button>
                </div>
              )}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}

type OpenIdx = number | 'all' | null

export default function Header({ vendor, categories, subscription, campaigns, mobileMenuOpen, setMobileMenuOpen }: { vendor?: any, categories?: any, subscription?: any, campaigns?: any, mobileMenuOpen: boolean, setMobileMenuOpen: (open: boolean) => void }) {
  const { data: session } = useSession()
  const { cartCount, validateCart, isValidating } = useCart()
  const search = useSearch()
  const { query, setQuery, runSearch } = search
  const { navigation } = useNavigation({ vendor, subscription, campaigns, categories, icons: { loyalty: Medal } })
  const pathname = usePathname() || ''
  const router = useRouter()

  const [openIdx, setOpenIdx] = useState<OpenIdx>(null)
  const [searchFocused, setSearchFocused] = useState(false)
  const [cartOpen, setCartOpen] = useState(false)
  const closeT = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Bar is position:fixed so macOS rubber-band overscroll can't drag it (sticky rides the bounce).
  // Slide it up via `top` (clamped >=0, capped at the SubHeader height) so only the SubHeader scrolls
  // away and the nav stays pinned. The fixed bar reserves no space, so an invisible normal-flow GHOST
  // of the bar (below) holds its EXACT height on the SSR first paint — no measurement, no flash.
  const subHeaderRef = useRef<HTMLDivElement>(null)
  const subHeaderHeightRef = useRef(0)
  const [barShift, setBarShift] = useState(0)
  useEffect(() => {
    const el = subHeaderRef.current
    if (!el) return
    const update = () => { subHeaderHeightRef.current = el.offsetHeight }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  useEffect(() => {
    let raf = 0
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        const y = Math.max(0, window.scrollY) // clamp overscroll -> bar never rides the bounce
        setBarShift(Math.min(y, subHeaderHeightRef.current))
      })
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  const enter = (i: OpenIdx) => {
    if (closeT.current) { clearTimeout(closeT.current); closeT.current = null }
    setOpenIdx(i)
  }
  const leave = () => {
    closeT.current = setTimeout(() => setOpenIdx(null), 80)
  }

  const showLogin = isCustomerAuthActive(vendor)
  const isAuthed = !!session?.user?.id
  const canPlaceOrder = vendor?.canPlaceOrder !== false
  const allSections = navigation.catalog?.sections || []
  const sections = allSections.slice(0, 3)
  const links = (navigation.links || []).filter((item: any) => !item.isHighlight)

  const openCart = async () => {
    setCartOpen(true)
    if (cartCount > 0 && !isValidating) {
      try { await validateCart() } catch (e) { console.error('Error validating cart:', e) }
    }
  }

  return (
    <>
      <div className="fixed inset-x-0 z-40" style={{ top: -barShift }}>
        <div ref={subHeaderRef}>
          <SubHeader vendor={vendor} />
        </div>
        <header
          className="w-full backdrop-blur-xl"
          style={{ background: 'rgba(8,8,14,0.55)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          {/* Main row: logo · search · actions */}
          <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 flex items-center gap-3 lg:gap-6 h-16">
            <button onClick={() => setMobileMenuOpen(true)} aria-label="Цэс" className="lg:hidden -ml-1 w-10 h-10 inline-flex items-center justify-center rounded-full transition-colors hover:bg-white/5" style={{ color: 'white' }}>
              <Menu className="h-5 w-5" />
            </button>

            <a href={navigateUrl('/', pathname)} className="flex items-center shrink-0">
              {vendor?.logo ? (
                <img src={formatImageURL(vendor.logo)} alt={vendor?.name} className="h-9 max-w-[190px] w-auto object-contain" />
              ) : (
                <span className="text-[20px] lg:text-[22px] font-semibold text-white tracking-[-0.02em]" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                  {vendor?.name || 'Shop'}
                </span>
              )}
            </a>

            <div className="hidden lg:block flex-1 max-w-2xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 z-10" style={{ color: 'rgba(255,255,255,0.5)' }} />
                <input
                  ref={searchInputRef}
                  placeholder="Бүтээгдэхүүн хайх…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 180)}
                  onKeyDown={e => { if (e.key === 'Enter') runSearch(); else if (e.key === 'Escape') (e.target as HTMLInputElement).blur() }}
                  className="w-full h-10 pl-9 pr-16 rounded-full text-[13px] outline-none transition-colors"
                  style={{
                    background: searchFocused ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.035)',
                    border: `1px solid ${searchFocused ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.07)'}`,
                    color: 'rgba(255,255,255,0.95)',
                  }}
                />
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono tracking-wider z-10 hidden xl:inline"
                  style={{ color: 'rgba(255,255,255,0.4)' }}
                >
                ⌘K
                </span>
                {searchFocused && <SearchPanel pathname={pathname} isAuthed={isAuthed} onClose={() => setSearchFocused(false)} search={search} />}
              </div>
            </div>

            <div className="ml-auto flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => setSearchFocused(true)}
                aria-label="Хайх"
                className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-full transition-colors hover:bg-white/10"
                style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}
              >
                <Search className="h-4 w-4" />
              </button>
              {showLogin && (
                <a
                  href={navigateUrl(isAuthed ? '/wishlist' : '/login?url=/wishlist', pathname)}
                  aria-label="Хадгалсан"
                  className="hidden md:inline-flex w-9 h-9 items-center justify-center rounded-full transition-colors hover:bg-white/5"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  <Heart className="h-4.5 w-4.5" />
                </a>
              )}
              {showLogin && (
                isAuthed ? (
                  <a
                    href={navigateUrl('/profile', pathname)}
                    aria-label="Профайл"
                    title="Профайл"
                    className="hidden md:inline-flex h-9 pl-1 pr-3 items-center gap-2 rounded-full transition-colors hover:bg-white/5"
                    style={{ color: 'white' }}
                  >
                    {session?.user?.avatar ? (
                      <img src={formatImageURL(session.user.avatar)} alt="profile" className="h-7 w-7 rounded-full object-cover" />
                    ) : (
                      <Avatar size={28} variant="marble" name={session?.user?.email || undefined} />
                    )}
                    {session?.user?.firstName && (
                      <span className="hidden lg:inline text-[13px] font-medium truncate max-w-32">{session.user.firstName}</span>
                    )}
                  </a>
                ) : (
                  <a
                    href={navigateUrl('/login', pathname)}
                    aria-label="Нэвтрэх"
                    title="Нэвтрэх"
                    className="hidden md:inline-flex w-9 h-9 items-center justify-center rounded-full transition-colors hover:bg-white/5"
                    style={{ color: 'rgba(255,255,255,0.7)' }}
                  >
                    <UserIcon className="h-4.5 w-4.5" />
                  </a>
                )
              )}
              {canPlaceOrder && (
                <button
                  onClick={openCart}
                  aria-label="Сагс"
                  className="ml-1 h-9 pl-3 pr-4 inline-flex items-center gap-2 rounded-full text-[13px] font-medium transition-colors hover:bg-white/10"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}
                >
                  <ShoppingCart className="h-4 w-4" />
                  <span className="hidden sm:inline">Сагс</span>
                  {cartCount > 0 && (
                    <span className="ml-0.5 text-[10px] font-semibold rounded-full h-4 min-w-4 px-1 inline-flex items-center justify-center tabular-nums" style={{ background: 'white', color: '#0a0a0c' }}>
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Categories bar */}
          <div className="relative hidden lg:block" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 flex items-center gap-1 h-11 overflow-x-auto scrollbar-hide" onMouseLeave={leave}>
              <button
                onMouseEnter={() => enter('all')}
                onClick={() => { window.location.href = navigateUrl('/product', pathname) }}
                className="h-8 px-3.5 inline-flex items-center gap-2 rounded-full text-[12px] font-medium shrink-0"
                style={{ background: 'white', color: '#0a0a0c' }}
              >
                <Menu className="h-3.5 w-3.5" />Бүх ангилал
              </button>
              {sections.map((s: any, i: number) => (
                <button
                  key={s.id || s.name}
                  onMouseEnter={() => enter(i)}
                  onClick={() => { window.location.href = s.href }}
                  className="h-8 px-3 inline-flex items-center gap-1 rounded-full text-[12px] shrink-0 transition-colors"
                  style={{
                    color: openIdx === i ? 'white' : 'rgba(255,255,255,0.75)',
                    background: openIdx === i ? 'rgba(255,255,255,0.06)' : 'transparent',
                  }}
                >
                  {s.name}
                  <ChevronDown
                    className="h-3 w-3 transition-transform duration-200"
                    style={{ transform: openIdx === i ? 'rotate(180deg)' : 'none' }}
                  />
                </button>
              ))}
              {links.map((item: any) => (
                <a
                  key={item.name}
                  href={navigateUrl(item.href, pathname)}
                  onMouseEnter={() => enter(null)}
                  onClick={(e) => { e.preventDefault(); router.push(navigateUrl(item.href, pathname)) }}
                  className="h-8 px-3 inline-flex items-center gap-1.5 rounded-full text-[12px] shrink-0 whitespace-nowrap transition-colors hover:bg-white/5"
                  style={{ color: item.isSale ? '#fca5a5' : 'rgba(255,255,255,0.6)' }}
                >
                  {item.isCampaign && <Sparkles className="h-3 w-3" />}
                  {item.name}
                </a>
              ))}

              {openIdx !== null && (
                <div onMouseEnter={() => enter(openIdx)}>
                  {openIdx === 'all'
                    ? <AllMenu sections={allSections} pathname={pathname} />
                    : <MegaMenu key={sections[openIdx as number]?.name} section={sections[openIdx as number]} pathname={pathname} />
                  }
                </div>
              )}
            </div>
          </div>

          {vendor?.isDanVerified === false && (
            <div style={{ background: 'rgba(120,53,15,0.4)', borderTop: '1px solid rgba(252,211,77,0.2)' }}>
              <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 h-9 flex items-center justify-between text-[12px]" style={{ color: '#fcd34d' }}>
                <div className="flex items-center gap-2 min-w-0">
                  <ShieldAlert className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
                  <span className="truncate">
                    <span className="font-semibold">Энэ дэлгүүр баталгаажаагүй байна.</span>
                    <span className="hidden sm:inline opacity-80"> Худалдан авалт хийхдээ анхаарна уу.</span>
                  </span>
                </div>
                <a href={navigateUrl('/status', pathname)} className="inline-flex items-center gap-1 font-semibold hover:underline shrink-0">
                Дэлгэрэнгүй
                  <ChevronRight className="h-3 w-3" />
                </a>
              </div>
            </div>
          )}
        </header>
      </div>
      {/* Ghost: invisible normal-flow mirror of the fixed bar — reserves its EXACT height on the SSR
          first paint so content never sits behind the bar (no JS measurement, no hydration flash).
          Keep the row heights/breakpoints/conditions in sync with the real bar above. */}
      <div aria-hidden className="invisible pointer-events-none">
        <SubHeader vendor={vendor} />
        <div className="border-b border-white/10">
          <div className="h-16" />
          <div className="hidden lg:block h-11 border-t border-white/10" />
          {vendor?.isDanVerified === false && <div className="h-9 border-t border-white/10" />}
        </div>
      </div>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} pathname={pathname} vendor={vendor} />
      <MobileDrawer open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} links={links} sections={allSections} pathname={pathname} showLogin={showLogin} vendor={vendor} />
    </>
  )
}
